<template>
  <div class="ui-input__wrapper" :class="{ 'ui-input__wrapper_error': hasError }">
    <slot name="label">
      <ui-label
        v-if="label"
        :name="name"
        :plan-limits="planLimits"
        :text="label"
        :help-tooltip="labelHelp"
      />
    </slot>

    <v-text-field
      ref="textFieldRef"
      :model-value="inputValue"
      @update:model-value="onInput"
      :class="{
        'ui-input': true,
        'ui-input--text': true,
        'ui-input--readonly': readonly,
        'ui-input--controls-hide-details': hideDetails > 0,
        'ui-input--controls-hide-details-compensate': hideDetails === 2,
        'ui-input--has-prepend-inner-icon': prependInnerIcon,
        'ui-input--has-append-inner-icon': appendInnerIcon,
        'mt-4': label || slots['label'],
      }"
      :name="name"
      :placeholder="placeholder"
      :error-messages="resolvedErrorMessages"
      :disabled="disabled"
      :prepend-icon="prependIconWithClass"
      :prepend-inner-icon="prependInnerIconWithClass"
      :append-icon="appendIconWithClass"
      :append-inner-icon="appendInnerIconWithClass"
      :append-outer-icon="appendOuterIconWithClass"
      :return-masked-value="returnMaskedValue"
      :rows="rows"
      :prefix="prefix"
      :suffix="suffix"
      :mask="mask"
      :autofocus="autofocus"
      :type="type"
      @click="handleClick"
      :readonly="readonly || (showPlanLimitsIcon && !planLimitsEditable)"
      :autocomplete="autocomplete"
      :maxlength="maxlength"
      @keyup="emit('keyup', $event)"
      @focus="emit('focus', $event)"
      @blur="emit('blur', $event)"
      @click:append="emit('click:append', $event)"
      @click:append-inner="emit('click:append-inner', $event)"
      @change="emit('change', $event)"
    >
      <template v-if="hasAppendSlot" #append-inner>
        <slot name="append" />
      </template>
    </v-text-field>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useSlots } from 'vue';
import { usePlanLimits } from '@/composables';

const props = withDefaults(
  defineProps<{
    value?: string;
    modelValue?: string | number | null;
    // 0 -- not hide errors block
    // 1 -- hide errors block
    // 2 -- hide errors block, compensate hide
    hideDetails?: number;
    placeholder?: string;
    prefix?: string;
    suffix?: string;
    autocomplete?: string;
    errorMessages?: string[];
    hasErrors?: boolean;
    disabled?: boolean;
    prependIcon?: string;
    prependInnerIcon?: string;
    appendIcon?: string;
    appendInnerIcon?: string;
    appendOuterIcon?: string;
    label?: string;
    labelHelp?: string;
    name?: string;
    returnMaskedValue?: string;
    rows?: number;
    mask?: string;
    autofocus?: string | boolean;
    type?: string;
    maxlength?: number;
    readonly?: boolean;
    planLimits?: import('@/composables').PlanLimitsProp;
    planLimitsEditable?: boolean;
  }>(),
  {
    hideDetails: 0,
    planLimitsEditable: false,
  },
);

const emit = defineEmits<{
  input: [value: string | number];
  'update:modelValue': [value: string | number];
  keyup: [event: Event];
  focus: [event: Event];
  blur: [event: Event];
  'click:append': [event: Event];
  'click:append-inner': [event: Event];
  change: [value: unknown];
}>();

const slots = useSlots();
const hasAppendSlot = computed(() => !!slots.append);
const textFieldRef = ref();

const { showPlanLimitsIcon, handleClickPlanLimits } = usePlanLimits(
  () => props.planLimits,
  () => props.planLimitsEditable,
);

const iconPrefix = 'ui-input__icon';

const prependIconWithClass = computed(() =>
  props.prependIcon ? `${props.prependIcon} ${iconPrefix}` : props.prependIcon,
);
const prependInnerIconWithClass = computed(() =>
  props.prependInnerIcon
    ? `${props.prependInnerIcon} ${iconPrefix}`
    : props.prependInnerIcon,
);
const appendIconWithClass = computed(() =>
  props.appendIcon ? `${props.appendIcon} ${iconPrefix}` : props.appendIcon,
);
const appendInnerIconWithClass = computed(() =>
  props.appendInnerIcon
    ? `${props.appendInnerIcon} ${iconPrefix}`
    : props.appendInnerIcon,
);
const appendOuterIconWithClass = computed(() =>
  props.appendOuterIcon
    ? `${props.appendOuterIcon} ${iconPrefix}`
    : props.appendOuterIcon,
);

const hasError = computed(() => !!resolvedErrorMessages.value?.length);

const resolvedErrorMessages = computed(() => {
  if (props.errorMessages?.length) {
    return props.errorMessages;
  }

  return props.hasErrors ? ['error'] : undefined;
});

const inputValue = computed(() => {
  const v =
    props.modelValue !== undefined && props.modelValue !== null
      ? props.modelValue
      : props.value;
  return v === undefined || v === null ? '' : String(v);
});

const blur = () => {
  const textField = textFieldRef.value as { blur?: () => void };
  textField?.blur?.();
};

const onInput = (e: string | number | Event) => {
  const val = (e as Event)?.target?.value ?? (e as string | number);
  emit('input', val);
  emit('update:modelValue', val);
};

const handleClick = (e: Event) => {
  if (showPlanLimitsIcon.value && !props.planLimitsEditable) {
    e.preventDefault();
    return handleClickPlanLimits();
  }
};

defineExpose({ blur });
</script>

<style>
/*safari*/
@media not all and (min-resolution: 0.001dpcm) {
  @media {
    .ui-input.v-input--is-disabled input {
      color: var(--color-black) !important;
    }
  }
}
</style>
<style lang="scss" scoped>
.ui-input--readonly {
  :deep(.v-input__slot),
  :deep(.v-field) {
    border-top: none;
    border-left: none;
    border-right: none;
    border-radius: 0 !important;
  }
  :deep(input) {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
<style lang="scss">
.v-field__input::placeholder {
  color: var(--color-gray40) !important;
  opacity: 1;
}

.ui-input {
  @import '@/styles/ui/input-general.scss';
  @import '@/styles/ui/input-like-default.scss';
  .v-text-field__suffix {
    margin-right: 4px;
    margin-left: 0;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.87);
    opacity: 1 !important;
    min-height: auto !important;
    height: auto !important;
    padding: 0 !important;
    align-self: center;
  }

  .v-text-field__prefix {
    margin-left: 4px;
    margin-right: 0;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.87);
    opacity: 1 !important;
    min-height: auto !important;
    height: auto !important;
    padding: 0 !important;
    align-self: center;
  }

  .v-text-field__prefix__text,
  .v-text-field__suffix__text {
    line-height: 1;
  }

  &.v-text-field--prefixed.ui-input--text {
    .v-field {
      --v-field-padding-start: 0;
    }

    input {
      padding-left: 4px;
    }
  }

  &.v-text-field--suffixed.ui-input--text {
    flex: 0 0 auto;
    width: auto;

    .v-field {
      --v-field-padding-end: 0;
    }

    .v-field__input {
      flex: 1 1 auto;
      width: 138px;
      min-width: 138px;
    }

    input {
      padding-right: 4px;
    }
  }

  &.ui-input--text.v-text-field {
    @include inputLikeDefaultIdle;

    /* Vuetify 3: custom border/background on .v-field */
    .v-field__outline,
    .v-field__overlay {
      display: none !important;
    }

    .v-field__field,
    .v-field__input {
      background: transparent !important;
    }

    .v-input__slot,
    .v-field {
      @include inputLikeDefaultBorderIdle;
      --v-input-control-height: 30px;
      min-height: 30px;
      border: solid 1px var(--color-gray20) !important;
      background-color: var(--color-white) !important;
      border-radius: 6px !important;
    }

    .v-field__input {
      min-height: 30px !important;
      height: 30px;
      max-height: 30px;
    }

    &.v-input--focused .v-input__slot,
    &.v-input--focused .v-field,
    .v-field.v-field--focused {
      @include inputLikeDefaultBorderFocused;
      border-color: var(--color-green) !important;
    }

    &.ui-input--readonly {
      .v-input__slot,
      .v-field,
      &.v-input--focused .v-field,
      .v-field.v-field--focused {
        border: none !important;
        border-bottom: 1px solid var(--color-gray20) !important;
        border-radius: 0 !important;
        background-color: var(--color-white) !important;
      }

      input {
        padding-left: 0 !important;
        padding-right: 0 !important;
        color: rgba(0, 0, 0, 0.87) !important;
      }
    }

    &.error--text .v-input__slot,
    &.error--text .v-field,
    &.v-input--error .v-input__slot,
    &.v-input--error .v-field {
      @include inputLikeDefaultBorderError;
      border-color: var(--color-red) !important;
    }

    &.v-input--is-disabled {
      .v-input__slot,
      .v-field {
        @include inputLikeDefaultBorderDisabled;
        border-color: var(--color-gray10) !important;
      }

      input {
        @include inputLikeDefaultInputDisabled;
      }
    }

    input {
      border: none !important;
      background-color: transparent !important;
      box-shadow: none !important;
      height: 30px !important;
      max-height: 30px;
      padding-left: 8px;
      padding-right: 4px;
      line-height: 30px;
      border-radius: 6px;
    }

    &.ui-input--has-append-inner-icon input {
      padding-right: 20px;
    }

    &.ui-input--has-prepend-inner-icon input {
      padding-left: 24px;
    }

    &.v-input--focused input {
      border: none !important;
    }

    &.error--text input,
    &.v-input--error input {
      border: none !important;
    }
  }

  padding-top: 0;
  margin-top: 0;

  &--controls-hide-details {
    height: 30px;
  }

  input {
    color: var(--color-gray40);
    padding-left: 8px;
    padding-right: 4px;
    height: 30px;
    max-height: 30px;
    min-width: 0;

    //chrome autocomplete issue
    border-radius: 6px;
  }

  &--has-prepend-inner-icon {
    input {
      padding-left: 24px;
    }
  }

  .v-input__prepend-inner,
  .v-field__prepend-inner {
    position: absolute;
    left: 8px;
    margin-top: 2px;
    padding: 0 4px 0 0;
    height: 24px;
    align-items: center;
  }

  &.v-input--is-disabled {
    input {
      color: var(--color-gray40);
    }
  }

  .v-input__slot,
  .v-field {
    margin-bottom: 0;
  }

  .v-input__icon--prepend-inner,
  .v-field__prepend-inner > .v-icon {
    min-width: 12px;
    width: 0;
  }

  .v-icon {
    color: inherit;
  }

  .v-input__append-inner,
  .v-field__append-inner {
    position: absolute;
    top: 0;
    right: 0;
    align-self: start;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: content-box;
    height: 24px;
    min-width: 24px;
    margin-top: 2px;
    padding: 0 0 0 4px;
  }

  .v-field__append-inner > .v-icon {
    align-self: center;
    width: auto;
    height: 13px;
    min-width: 0;
    line-height: 13px;
  }

  .v-field {
    position: relative;
  }

  &__icon {
    font-size: 13px;
    //!important: избавляемся от стилей .primary--text
    color: var(--color-gray40) !important;

    .v-input--focused & {
      //!important: избавляемся от стилей .primary--text
      color: var(--color-graphite) !important;
    }

    .v-input--error & {
      color: #ff5252 !important;
    }
  }
}
</style>
